import React, { useMemo, useState } from "react";
import {
  Calendar,
  Check,
  Paperclip,
  CalendarClock,
  CalendarRange,
} from "lucide-react";
import { SubTask } from "../../../types";
import { isValidWorkHoursSlot } from "../../../lib/workingHours";
import { useAttachmentViewer } from "../../../hooks/useAttachmentViewer";
import {
  useUploadSubtaskAttachment,
} from "../../../hooks/mutations/useUploadAttachment";
import {
  useDeleteSubtaskAttachment,
} from "../../../hooks/mutations/useDeleteAttachment";
import {
  useRenameSubtaskAttachment,
} from "../../../hooks/mutations/useRenameAttachment";
import RichTextEditor from "../../forms/LazyRichTextEditor";
import AttachmentList from "../../forms/AttachmentList";
import AttachmentUploader from "../../forms/AttachmentUploader";
import AttachmentViewer from "../AttachmentViewer";
import TimePicker from "../../forms/TimePicker";
import ScheduledDateField from "../../forms/ScheduledDateField";
import WorkingHoursToggle from "../../forms/WorkingHoursToggle";
import Tooltip from "../../ui/Tooltip";
import DurationPicker from "./DurationPicker";
import SubtaskLinksSection from "./SubtaskLinksSection";
import TagPicker from "./TagPicker";

export interface SubtaskDetailProps {
  /** The sub-task currently open in the dialog's breadcrumb sub-view. */
  subtask: SubTask;
  userId?: string;
  weekStartDay?: number | null;
  /** Whether the "Pick on calendar" affordance can be offered (desktop only). */
  pickerAvailable: boolean;
  /** Patch fields on the open sub-task. Persistence is the dialog's job. */
  onChange: (updates: Partial<SubTask>) => void;
  /** Open the schedule picker against this sub-task. */
  onOpenPicker: () => void;
  /** Toggle the dialog's shared tag popover, anchored to the given element. */
  onToggleTagPopover: (anchor: HTMLElement) => void;
  /**
   * The current slot falls outside working hours, so the toggle was refused.
   * The dialog owns the explanatory modal.
   */
  onBlockWorkHours: () => void;
}

/**
 * The sub-task detail pane — the two-column editor the dialog swaps in when a
 * sub-task is opened from the list.
 *
 * Owns its own draft state (description editing, the link form, the duration
 * text field, the in-flight attachment delete) and calls the sub-task mutation
 * hooks directly, so the dialog no longer carries a parallel set of sub-task
 * handlers alongside the task's own.
 */
const SubtaskDetail: React.FC<SubtaskDetailProps> = ({
  subtask,
  userId,
  weekStartDay,
  pickerAvailable,
  onChange,
  onOpenPicker,
  onToggleTagPopover,
  onBlockWorkHours,
}) => {
  const uploadSubtaskAttachment = useUploadSubtaskAttachment();
  const deleteSubtaskAttachment = useDeleteSubtaskAttachment();
  const renameSubtaskAttachment = useRenameSubtaskAttachment();

  const [isDescriptionEditing, setIsDescriptionEditing] = useState(false);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<
    string | null
  >(null);
  const { viewerIndex, openViewer, closeViewer } = useAttachmentViewer();

  // SubtaskAttachment carries `subtask_id`, not `task_id` — AttachmentList
  // and AttachmentViewer are shared with the task-level attachments, so they
  // expect the TaskAttachment shape. Graft on the parent task's id to match
  // it; only the `id` field is actually read for delete/rename/preview.
  // Memoized so it's a stable reference across renders that don't touch
  // `subtask.attachments` — AttachmentViewer relies on identity to tell a
  // real list change from an unrelated re-render.
  const subtaskAttachments = useMemo(
    () =>
      (subtask.attachments || []).map((a) => ({
        ...a,
        task_id: subtask.task_id,
      })),
    [subtask.attachments, subtask.task_id],
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 p-3 sm:p-6 h-full">
      {/* LEFT COLUMN - Subtask Content */}
      <div className="lg:col-span-2 space-y-4 sm:space-y-6">
        {/* Subtask Title */}
        <div>
          <label className="block text-sm font-medium text-warm-text-secondary mb-2">
            Title
          </label>
          <input
            type="text"
            value={subtask.title}
            onChange={(e) => onChange({ title: e.target.value })}
            className="w-full bg-warm-bg-elevated border border-warm-border rounded-soft px-4 py-3 text-lg font-medium text-warm-text-primary placeholder-warm-text-tertiary focus:outline-none focus:ring-2 focus:ring-warm-blue focus:border-transparent transition-all duration-150 ease-in-out"
            placeholder="Subtask title..."
            autoFocus
          />
        </div>

        {/* Subtask Description */}
        <div className="flex-1 flex flex-col min-h-[200px]">
          <label className="block text-sm font-medium text-warm-text-secondary mb-2">
            Description
          </label>
          {!isDescriptionEditing ? (
            <div
              onClick={() => setIsDescriptionEditing(true)}
              className="flex-1 bg-warm-bg-elevated border border-warm-border rounded-soft px-4 py-3 cursor-text hover:border-warm-blue/50 dark:hover:border-warm-blue/50 transition-colors min-h-[120px]"
            >
              {subtask.description ? (
                <div
                  className="task-brief text-warm-text-primary"
                  dangerouslySetInnerHTML={{ __html: subtask.description }}
                />
              ) : (
                <p className="text-warm-text-tertiary italic">
                  Click to add a description...
                </p>
              )}
            </div>
          ) : (
            <div className="flex-1 flex flex-col">
              <RichTextEditor
                content={subtask.description}
                onChange={(content) => onChange({ description: content })}
                placeholder="Add a description..."
                userId={userId}
              />
              <button
                onClick={() => setIsDescriptionEditing(false)}
                className="mt-2 self-end px-3 py-1.5 bg-warm-blue text-white rounded-soft text-sm hover:bg-warm-blue-dark transition-colors"
              >
                Done
              </button>
            </div>
          )}
        </div>

        {/* Subtask Attachments */}
        <div>
          <label className="block text-sm font-medium text-warm-text-secondary mb-2 flex items-center gap-2">
            <Paperclip size={16} />
            Attachments
            {subtask.attachments && subtask.attachments.length > 0 && (
              <span className="text-xs text-warm-text-tertiary font-normal">
                ({subtask.attachments.length})
              </span>
            )}
          </label>
          <div className="space-y-2">
            <AttachmentUploader
              currentCount={subtask.attachments?.length || 0}
              onUpload={async (file) => {
                const attachment = await uploadSubtaskAttachment(
                  subtask.id,
                  file,
                );
                if (attachment) {
                  onChange({
                    attachments: [...(subtask.attachments || []), attachment],
                  });
                }
              }}
            />
            <AttachmentList
              attachments={subtaskAttachments}
              onDelete={async (attachmentId) => {
                setDeletingAttachmentId(attachmentId);
                const success = await deleteSubtaskAttachment(attachmentId);
                if (success) {
                  onChange({
                    attachments: subtask.attachments?.filter(
                      (a) => a.id !== attachmentId,
                    ),
                  });
                }
                setDeletingAttachmentId(null);
              }}
              onRename={async (attachmentId, newName) => {
                const success = await renameSubtaskAttachment(
                  attachmentId,
                  newName,
                );
                if (success) {
                  const trimmed = newName.trim();
                  onChange({
                    attachments: subtask.attachments?.map((a) =>
                      a.id === attachmentId
                        ? { ...a, file_name: trimmed }
                        : a,
                    ),
                  });
                }
                return success;
              }}
              onOpen={openViewer}
              openIndex={viewerIndex}
              isDeleting={deletingAttachmentId}
            />
          </div>
          {viewerIndex !== null && (
            <AttachmentViewer
              key={viewerIndex}
              attachments={subtaskAttachments}
              initialIndex={viewerIndex}
              onClose={closeViewer}
            />
          )}
        </div>

        {/* Subtask Links */}
        <SubtaskLinksSection subtask={subtask} onChange={onChange} />
      </div>

      {/* RIGHT COLUMN - Subtask Metadata */}
      <div className="space-y-4 sm:space-y-5">
        {/* Priority */}
        <div>
          <label className="block text-xs font-medium text-warm-text-tertiary mb-2 uppercase tracking-wider">
            Priority
          </label>
          <div className="flex gap-2">
            {[
              { value: 1, label: "High", color: "border-l-red-700" },
              { value: 2, label: "Med", color: "border-l-orange-500" },
              { value: 3, label: "Low", color: "border-l-yellow-400" },
            ].map((p) => (
              <button
                key={p.value}
                onClick={() => onChange({ priority: p.value })}
                className={`flex-1 px-3 py-2 rounded-card border-l-4 text-sm transition-all duration-150 ease-in-out text-left ${
                  subtask.priority === p.value
                    ? `${p.color} bg-warm-bg-card border border-warm-border shadow-soft-md text-warm-text-primary font-medium`
                    : `${p.color} bg-warm-bg-elevated border border-warm-border text-warm-text-secondary opacity-60 hover:opacity-90 hover:shadow-soft-sm`
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tags */}
        <TagPicker
          selectedTags={subtask.tags || []}
          onToggleTagPopover={onToggleTagPopover}
        />

        {/* Schedule */}
        <div>
          <label className="block text-xs font-medium text-warm-text-tertiary mb-2 uppercase tracking-wider">
            Schedule
          </label>
          <div className="flex flex-col gap-2">
            <div className="flex items-stretch gap-2">
              <div className="relative flex-1">
                <ScheduledDateField
                  value={subtask.scheduled_date || null}
                  onChange={(newDate) => {
                    const cleared = newDate
                      ? {}
                      : { scheduled_time: null, duration: null };
                    onChange({ scheduled_date: newDate, ...cleared });
                  }}
                  restrictToWorkingHours={!!subtask.working_hours_only}
                  weekStartDay={weekStartDay}
                />
              </div>
              {pickerAvailable && (
                <Tooltip content="Pick on calendar">
                  <button
                    type="button"
                    aria-label="Pick on calendar"
                    onClick={onOpenPicker}
                    className="hidden md:inline-flex items-center justify-center px-3 bg-warm-bg-elevated border border-warm-border rounded-soft text-warm-text-secondary hover:border-warm-blue/50 dark:hover:border-warm-blue/50 transition-all duration-150 ease-in-out"
                  >
                    <CalendarRange size={16} />
                  </button>
                </Tooltip>
              )}
            </div>
            {subtask.scheduled_date && (
              <TimePicker
                value={subtask.scheduled_time || null}
                onChange={(newTime) => {
                  const cleared = newTime ? {} : { duration: null };
                  onChange({ scheduled_time: newTime, ...cleared });
                }}
                restrictToWorkingHours={!!subtask.working_hours_only}
              />
            )}
            {subtask.scheduled_date && subtask.scheduled_time && (
              <DurationPicker
                value={subtask.duration ?? null}
                onChange={(duration) => onChange({ duration })}
              />
            )}
          </div>
        </div>

        {/* Carry over */}
        {subtask.scheduled_date && (
          <div>
            <button
              type="button"
              role="switch"
              aria-checked={subtask.persistent}
              onClick={() => onChange({ persistent: !subtask.persistent })}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-soft text-sm transition-all duration-150 ease-in-out text-left ${
                subtask.persistent
                  ? "bg-warm-blue/10 border border-warm-blue/30"
                  : "bg-warm-bg-elevated border border-warm-border hover:border-warm-blue/50 dark:hover:border-warm-blue/50"
              }`}
            >
              <CalendarClock
                size={16}
                className={
                  subtask.persistent
                    ? "text-warm-blue flex-shrink-0"
                    : "text-warm-text-tertiary flex-shrink-0"
                }
              />
              <div className="flex-1 min-w-0">
                <div
                  className={
                    subtask.persistent
                      ? "font-medium text-warm-blue"
                      : "text-warm-text-secondary"
                  }
                >
                  Carry over
                </div>
                <div className="text-[11px] text-warm-text-tertiary leading-tight mt-0.5">
                  Moves to today each day until it is done. The time stays the same.
                </div>
              </div>
              <div
                className={`relative w-9 h-5 rounded-full transition-colors duration-150 ease-in-out flex-shrink-0 ${
                  subtask.persistent ? "bg-warm-blue" : "bg-warm-border"
                }`}
              >
                <div
                  className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-150 ease-in-out ${
                    subtask.persistent ? "translate-x-4" : "translate-x-0"
                  }`}
                />
              </div>
            </button>
          </div>
        )}

        {/* Working hours only (subtasks have no recurrence, so no
            mutual-exclusion gating). */}
        <div>
          <WorkingHoursToggle
            active={!!subtask.working_hours_only}
            onClick={() => {
              const turningOn = !subtask.working_hours_only;
              if (
                turningOn &&
                !isValidWorkHoursSlot(
                  subtask.scheduled_date,
                  subtask.scheduled_time,
                )
              ) {
                onBlockWorkHours();
                return;
              }
              onChange({ working_hours_only: turningOn });
            }}
          />
        </div>

        {/* Deadline */}
        <div>
          <label className="block text-xs font-medium text-warm-text-tertiary mb-2 uppercase tracking-wider">
            Deadline
          </label>
          <div className="relative">
            <Calendar
              className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-text-tertiary"
              size={16}
            />
            <input
              type="date"
              aria-label="Deadline date"
              value={subtask.deadline_date || ""}
              onChange={(e) =>
                onChange({ deadline_date: e.target.value || null })
              }
              className="w-full bg-warm-bg-elevated border border-warm-border rounded-soft pl-10 pr-3 py-2 text-sm text-warm-text-primary focus:outline-none focus:ring-2 focus:ring-warm-blue"
            />
          </div>
        </div>

        {/* Completion toggle */}
        <div>
          <label className="block text-xs font-medium text-warm-text-tertiary mb-2 uppercase tracking-wider">
            Status
          </label>
          <button
            onClick={() => onChange({ completed: !subtask.completed })}
            className={`w-full px-3 py-2 rounded-soft text-sm transition-all duration-150 ease-in-out text-left flex items-center gap-2 ${
              subtask.completed
                ? "bg-warm-blue/10 text-warm-blue border border-warm-blue/30"
                : "bg-warm-bg-elevated border border-warm-border text-warm-text-secondary hover:border-warm-blue/50"
            }`}
          >
            <div
              className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                subtask.completed
                  ? "bg-warm-blue border-warm-blue"
                  : "border-warm-text-tertiary"
              }`}
            >
              {subtask.completed && <Check size={10} className="text-white" />}
            </div>
            {subtask.completed ? "Completed" : "Mark complete"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubtaskDetail;
